import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * Tipi per la configurazione torneo
 */
interface TournamentConfig {
  tournamentType: string;
  pairingMode: string;
  maxParticipants: number;
}

interface HelpMessage {
  [tournamentType: string]: {
    [pairingMode: string]: string;
  };
}

/**
 * Componente per la gestione della modalità di accoppiamento tornei
 * Sostituisce tournament-pairing-mode.js con gestione reattiva
 */
@customElement('pm-pairing-selector')
export class PmPairingSelector extends LitElement {
  @property({ type: String }) tournamentTypeSelector = '#id_tournament_type';
  @property({ type: String }) pairingModeSelector = '#id_pairing_mode';  
  @property({ type: String }) maxParticipantsSelector = '#id_max_participants';

  @state() private currentConfig: TournamentConfig = {
    tournamentType: '',
    pairingMode: '',
    maxParticipants: 0
  };

  // Riferimenti ai campi del form
  private tournamentTypeField?: HTMLSelectElement;
  private pairingModeField?: HTMLSelectElement;
  private maxParticipantsField?: HTMLInputElement;

  // Messaggi di aiuto per ogni combinazione
  private readonly HELP_MESSAGES: HelpMessage = {
    'single_elimination': {
      'fixed_pairs': 'Coppie fisse: ogni coppia compete come team fisso attraverso il bracket.'
    },
    'round_robin': {
      'fixed_pairs': 'Coppie fisse: ogni coppia gioca contro tutte le altre coppie.'
    },
    'double_elimination': {
      'fixed_pairs': 'Coppie fisse: necessarie per mantenere la continuità competitiva nei due bracket.'
    }
  };

  static styles = css`
    :host {
      display: contents;
    }
    
    .pairing-help {
      display: flex;
      align-items: center;
      margin-top: 0.375rem;
      font-size: 0.875rem;
      color: #6c757d;
    }
    
    .pairing-help i {
      margin-right: 0.375rem;
    }
    
    .pairing-warning {
      margin-top: 0.5rem;
      padding: 0.75rem;
      background-color: #fff3cd;
      border: 1px solid #ffecb5;
      border-radius: 0.375rem;
      color: #664d03;
    }
    
    .fade-in {
      animation: fadeIn 0.3s ease-in;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.initializeFields();
  }

  firstUpdated() {
    this.setupEventListeners();
    this.updateFromCurrentValues();
  }

  /**
   * Inizializza i riferimenti ai campi del form
   */
  private initializeFields() {
    this.tournamentTypeField = document.querySelector(this.tournamentTypeSelector) as HTMLSelectElement;
    this.pairingModeField = document.querySelector(this.pairingModeSelector) as HTMLSelectElement;
    this.maxParticipantsField = document.querySelector(this.maxParticipantsSelector) as HTMLInputElement;
  }

  /**
   * Configura gli event listeners sui campi del form
   */
  private setupEventListeners() {
    if (this.tournamentTypeField) {
      this.tournamentTypeField.addEventListener('change', () => {
        this.updateTournamentType();
      });
    }

    if (this.pairingModeField) {
      this.pairingModeField.addEventListener('change', () => {
        this.updatePairingMode();
      });
    }

    if (this.maxParticipantsField) {
      this.maxParticipantsField.addEventListener('input', () => {
        this.updateMaxParticipants();
      });
      this.maxParticipantsField.addEventListener('change', () => {
        this.updateMaxParticipants();
      });
    }
  }

  /**
   * Aggiorna la configurazione dai valori correnti dei campi
   */
  private updateFromCurrentValues() {
    this.currentConfig = {
      tournamentType: this.tournamentTypeField?.value || '',
      pairingMode: this.pairingModeField?.value || '',
      maxParticipants: parseInt(this.maxParticipantsField?.value || '0') || 0
    };
    this.updateDisplay();
  }

  /**
   * Aggiorna il tipo di torneo
   */
  private updateTournamentType() {
    this.currentConfig = {
      ...this.currentConfig,
      tournamentType: this.tournamentTypeField?.value || ''
    };
    this.updateDisplay();
  }

  /**
   * Aggiorna la modalità di accoppiamento
   */
  private updatePairingMode() {
    this.currentConfig = {
      ...this.currentConfig,
      pairingMode: this.pairingModeField?.value || ''
    };
    this.updateDisplay();
  }

  /**
   * Aggiorna il numero massimo di partecipanti
   */
  private updateMaxParticipants() {
    this.currentConfig = {
      ...this.currentConfig,
      maxParticipants: parseInt(this.maxParticipantsField?.value || '0') || 0
    };
    this.updateDisplay();
  }

  /**
   * Aggiorna la visualizzazione dei messaggi di aiuto e warning
   */
  private updateDisplay() {
    this.removeExistingMessages();
    this.addHelpMessage();
    this.addWarningMessage();
  }

  /**
   * Rimuove messaggi esistenti dal DOM
   */
  private removeExistingMessages() {
    const existingHelp = document.querySelector('#pairing-mode-help');
    const existingWarning = document.querySelector('#pairing-mode-warning');
    
    if (existingHelp) {
      existingHelp.remove();
    }
    if (existingWarning) {
      existingWarning.remove();
    }
  }

  /**
   * Aggiunge messaggio di aiuto se appropriato
   */
  private addHelpMessage() {
    const { tournamentType, pairingMode } = this.currentConfig;
    
    if (tournamentType && pairingMode && 
        this.HELP_MESSAGES[tournamentType] && 
        this.HELP_MESSAGES[tournamentType][pairingMode]) {
      
      const helpText = this.HELP_MESSAGES[tournamentType][pairingMode];
      const helpElement = document.createElement('small');
      helpElement.id = 'pairing-mode-help';
      helpElement.className = 'form-text text-muted fade-in';
      helpElement.innerHTML = `<i class="fas fa-info-circle me-1"></i>${helpText}`;
      
      if (this.pairingModeField?.parentElement) {
        this.pairingModeField.parentElement.appendChild(helpElement);
      }
    }
  }

  /**
   * Aggiunge messaggio di warning se necessario
   */
  private addWarningMessage() {
    const { tournamentType, pairingMode, maxParticipants } = this.currentConfig;
    
    let warningMessage = '';
    
    // Logica per determinare warnings
    // Al momento non ci sono warnings specifici, ma la struttura è pronta
    
    if (warningMessage && this.pairingModeField?.parentElement) {
      const warningElement = document.createElement('div');
      warningElement.id = 'pairing-mode-warning';
      warningElement.className = 'alert alert-warning mt-2 fade-in';
      warningElement.innerHTML = warningMessage;
      
      this.pairingModeField.parentElement.appendChild(warningElement);
    }
  }

  /**
   * Metodo pubblico per aggiornare la configurazione programmaticamente
   */
  updateConfiguration(config: Partial<TournamentConfig>) {
    this.currentConfig = { ...this.currentConfig, ...config };
    this.updateDisplay();
  }

  /**
   * Ottiene la configurazione corrente
   */
  getConfiguration(): TournamentConfig {
    return { ...this.currentConfig };
  }

  render() {
    // Questo componente gestisce solo la logica
    // I campi del form esistono già nel DOM
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-pairing-selector': PmPairingSelector;
  }
}